"use client";

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Knopf } from "../knopf";
import { SchliessenSymbol } from "../modal";
import { fokussierbareElemente } from "../modal/modal-verhalten";
import { HornautFigurBild, KiSymbol } from "./hornaut-begleiter";
import { HORNAUT_KOPF_AVATAR } from "../hornaut-kopf";
import { TourDimmer } from "./tour-dimmer";
import { istMobil, leseRechteck, lochUmZiel, platziere, scrolleInsBild, type Bildschirm, type Rechteck } from "./tour-position";

/*
 * Tour-Overlay (Musterkatalog M-017, Design-Guide `tour-overlay.md`): Schritt-Popover am Ziel oder Zentral-Karte ohne Ziel,
 * auf Mobilgeräten (unter 768 px) ein Bottom-Sheet. Reiner Darstellungs-Baustein: Zustand, Navigation und Zielsuche liegen
 * beim Aufrufer (`apps/web/src/components/preline-tour-host.tsx`); der Baustein kennt weder Registry noch Router (Paketgrenze).
 *
 * Verhalten (Entscheid Koordination, Betreiber-Delegation 08.10.2026):
 *  - Das Ziel bleibt bedienbar (Loch im Dimmer); „Weiter“ geht immer, die Tour wartet nicht auf eine Handlung.
 *  - Esc beendet die Tour, auch über einem offenen Modal (Capture-Phase vor dem Modal); ein offenes Menü oder eine offene
 *    Feldliste bekommt Esc zuerst. Pfeil rechts/links wechselt den Schritt (nicht in Eingabefeldern), Enter auf dem Dialog selbst.
 *  - Der Fokus liegt auf „Weiter“, Tab bleibt im Popover; beim Beenden geht er an den Auslöser zurück.
 *  - Der Text des Schritts trägt die Selbstprüfung (kommt aus der Tour-Definition); der Baustein wertet nichts aus.
 * Ebene z-90 über den Modalen (z-70/z-80). Lage und Maße setzt der Baustein zur Laufzeit am Element (wie `dropdown-menue.tsx`).
 * Lucide-Symbole (R-F) als Inline-SVG, das Bausteinpaket führt keine Icon-Abhängigkeit.
 */

export interface TourSchrittAnzeige {
  titel: string;
  text: string;
  /** CSS-Selektor des Ziels; fehlt er, steht der Schritt als Zentral-Karte. */
  ziel?: string;
}

/** `ohne` = Schritt hat kein Ziel, `suche` = Ziel wird gesucht (nichts sichtbar), `da` = Ziel gefunden, `fehlt` = nicht gefunden. */
export type TourZielStatus = "ohne" | "suche" | "da" | "fehlt";

/** Popover am Ziel (ab 768 px frei platziert), Zentral-Karte (breiter) und Bottom-Sheet darunter; kein `top`/`left` im Sheet. */
const KARTE_ZIEL =
  "fixed z-90 flex flex-col bg-popover border border-popover-line shadow-md motion-reduce:transition-none md:top-0 md:start-0 md:w-96 md:rounded-xl max-md:inset-x-0 max-md:bottom-0 max-md:rounded-t-xl";
const KARTE_ZENTRAL =
  "fixed z-90 flex flex-col bg-popover border border-popover-line shadow-md motion-reduce:transition-none md:top-0 md:start-0 md:w-112 md:rounded-xl max-md:inset-x-0 max-md:bottom-0 max-md:rounded-t-xl";

/** Zentral-Karte mit Figur (Einleitung, Abschluss, nur bei KI an): 680 px, Abweichung AW-015b (Betreiber 09.10.2026, Variante C). */
const KARTE_ZENTRAL_FIGUR =
  "fixed z-90 flex flex-col bg-popover border border-popover-line shadow-md motion-reduce:transition-none md:top-0 md:start-0 md:w-170 md:rounded-xl max-md:inset-x-0 max-md:bottom-0 max-md:rounded-t-xl";
/**
 * Stehende Leiste, wenn das Hornaut-Panel die Karte ersetzt (Sheet mobil, Überlagerung 768 bis 1279 px). z-92 über dem Hornaut-Sheet
 * (z-91); mobil feste Höhe h-14 (56 px), das Sheet endet mit `bottom-14` genau darüber (hornaut-begleiter.tsx, beide Maße gehören zusammen).
 */
const LEISTE =
  "fixed z-92 max-md:h-14 inset-x-0 bottom-0 py-2 px-4 flex items-center justify-between gap-x-3 bg-popover border border-popover-line shadow-md md:inset-x-auto md:start-4 md:bottom-4 md:rounded-xl";

/**
 * Hornaut-Teile der Karte (optional; der Aufrufer übergibt sie nur bei KI an, Rolle ∧ Policy, R-D).
 * Ohne diese Prop ist der Baustein die Tour von M-017, unverändert.
 */
export interface TourHornaut {
  /** Knopf „Hornaut fragen“ im Fuß der Karte. */
  knopf: boolean;
  /** Hornaut-Zeile statt Knopf (Einleitung, Wortlaut kommt vom Aufrufer). */
  zeile?: string;
  /** Ganzkörper-Figur in der Zentral-Karte (nur Einleitung und Abschluss). */
  figur: boolean;
  /** Das Panel ist offen. */
  panelOffen: boolean;
  /** Panel offen und nicht angedockt: die Karte tritt zurück, es steht nur die Leiste „Zur Tour“. */
  ueberlagert: boolean;
  /** Breite des angedockten Panels in px (0 sonst): das Popover meidet diesen Streifen. */
  randRechts: number;
  beiFragen: () => void;
  beiPanelSchliessen: () => void;
  beiZurTour: () => void;
}

/** Abstand des Hinweises (Ziel nicht gefunden) zum Bildrand, Koordinate, keine Gestaltung. */
const HINWEIS_RAND = 16;

/** Selektor für Offenes, das Esc vor der Tour bekommt (wie `modal-verhalten.ts`). */
const ESC_VORRANG = '[role="menu"],[data-esc-eigen="offen"]';
const EINGABE = 'input,textarea,select,[contenteditable=""],[contenteditable="true"]';

/** Lucide `ChevronLeft`. */
function ZurueckSymbol() {
  return (
    <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

/** Lucide `ChevronRight`. */
function WeiterSymbol() {
  return (
    <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function gleich(a: Rechteck | null, b: Rechteck | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.links === b.links && a.oben === b.oben && a.breite === b.breite && a.hoehe === b.hoehe;
}

/** Sichtbare Fläche; ein angedocktes Hornaut-Panel nimmt rechts `randRechts` px weg. */
function lesBildschirm(randRechts = 0): Bildschirm {
  return { breite: document.documentElement.clientWidth - randRechts, hoehe: window.innerHeight };
}

export function TourOverlay({
  titel,
  schritte,
  index,
  zielStatus,
  beiWeiter,
  beiZurueck,
  beiBeenden,
  ausloeser,
  kopfzeile,
  hornaut,
}: {
  /** Hornaut-Teile (Knopf, Zeile, Figur, Panel-Zustand); fehlt sie, ist die Tour ohne Hornaut (KI aus). */
  hornaut?: TourHornaut;
  /** Titel der Tour (volltext, steht als natives `title` an der Kopfzeile). */
  titel: string;
  /** Kurzfassung für die einzeilige Kopfzeile (z. B. „Kapitel 1“); ohne Angabe steht der Titel. */
  kopfzeile?: string;
  schritte: TourSchrittAnzeige[];
  index: number;
  zielStatus: TourZielStatus;
  beiWeiter: () => void;
  beiZurueck: () => void;
  beiBeenden: () => void;
  /** Element, das beim Beenden den Fokus zurückbekommt; sonst das beim Einhängen fokussierte. */
  ausloeser?: HTMLElement | null;
}) {
  const [eingehaengt, setEingehaengt] = useState(false);
  useEffect(() => setEingehaengt(true), []);

  const schritt = schritte[index];
  const id = useId();
  const karte = useRef<HTMLDivElement>(null);
  const weiterKnopf = useRef<HTMLButtonElement>(null);
  const [rechteck, setRechteck] = useState<Rechteck | null>(null);
  const [bildschirm, setBildschirm] = useState<Bildschirm>({ breite: 1280, hoehe: 800 });
  const selektor = schritt?.ziel;
  const mitZiel = zielStatus === "da" && !!selektor;

  // Aktuelle Werte für die Tastenbehandlung (Listener bleibt über Schritte hinweg gleich).
  const aktuell = useRef({ beiWeiter, beiZurueck, beiBeenden, hornaut });
  useEffect(() => {
    aktuell.current = { beiWeiter, beiZurueck, beiBeenden, hornaut };
  }, [beiWeiter, beiZurueck, beiBeenden, hornaut]);
  const randRechts = hornaut?.randRechts ?? 0;
  const ueberlagert = !!hornaut?.ueberlagert;
  const hornautKnopf = useRef<HTMLButtonElement>(null);
  // Fokus-Rückgabe vom Panel: schließt es, geht der Fokus an den Knopf „Hornaut fragen“ (sonst auf „Weiter“).
  const panelWarOffen = useRef(false);
  const panelOffen = !!hornaut?.panelOffen;
  useEffect(() => {
    if (panelWarOffen.current && !panelOffen) (hornautKnopf.current ?? weiterKnopf.current)?.focus({ preventScroll: true });
    panelWarOffen.current = panelOffen;
  }, [panelOffen, ueberlagert]);

  // Fokus-Rückgabe: Auslöser merken (beim Einhängen), beim Beenden zurückgeben.
  const merker = useRef<HTMLElement | null>(null);
  const ausloeserRef = useRef<HTMLElement | null | undefined>(ausloeser);
  useEffect(() => {
    ausloeserRef.current = ausloeser;
  }, [ausloeser]);
  useEffect(() => {
    merker.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      const ziel = ausloeserRef.current ?? merker.current;
      if (ziel && ziel !== document.body && document.contains(ziel)) ziel.focus();
    };
  }, []);

  // Messen: Bildschirm und Zielrechteck. Nur Zustand setzen, wenn sich etwas ändert (kein Render-Kreislauf).
  const messen = useCallback(() => {
    const neu = lesBildschirm(randRechts);
    setBildschirm((alt) => (alt.breite === neu.breite && alt.hoehe === neu.hoehe ? alt : neu));
    const el = mitZiel && selektor ? document.querySelector(selektor) : null;
    const r = el ? leseRechteck(el) : null;
    setRechteck((alt) => (gleich(alt, r) ? alt : r));
  }, [mitZiel, selektor, randRechts]);

  // Ziel ins Bild holen und beobachten: Größe, Scrollen, Fenstergröße und Änderungen am Dokument (Modal öffnet, Ziel verschwindet).
  useLayoutEffect(() => {
    messen();
    if (!mitZiel || !selektor) return;
    const el = document.querySelector(selektor);
    if (el) {
      const reduziert = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      scrolleInsBild(el, lesBildschirm(randRechts), reduziert);
    }
    let rahmen = 0;
    const geplant = () => {
      if (rahmen) return;
      rahmen = window.requestAnimationFrame(() => {
        rahmen = 0;
        messen();
      });
    };
    window.addEventListener("resize", geplant);
    window.addEventListener("scroll", geplant, true);
    const groesse = typeof ResizeObserver === "function" ? new ResizeObserver(geplant) : null;
    if (el) groesse?.observe(el);
    const aenderung = typeof MutationObserver === "function" ? new MutationObserver(geplant) : null;
    aenderung?.observe(document.body, { childList: true, subtree: true });
    return () => {
      window.removeEventListener("resize", geplant);
      window.removeEventListener("scroll", geplant, true);
      groesse?.disconnect();
      aenderung?.disconnect();
      if (rahmen) window.cancelAnimationFrame(rahmen);
    };
  }, [messen, mitZiel, selektor, index, randRechts]);

  // Lage der Karte (Desktop) setzen; im Bottom-Sheet bestimmen die Klassen die Lage.
  const mobil = istMobil(bildschirm);
  const sichtbar = zielStatus !== "suche" && !!schritt;
  useLayoutEffect(() => {
    const el = karte.current;
    if (!el) return;
    if (mobil) {
      el.style.left = "";
      el.style.top = "";
      return;
    }
    const b = el.getBoundingClientRect();
    if ((zielStatus === "fehlt" || mitZiel) && !rechteck) {
      // Hinweis ohne Ziel: unten rechts, damit er die Mitte der Seite (die Handlung des Schritts) nicht verdeckt.
      el.style.left = `${Math.max(HINWEIS_RAND, Math.round(bildschirm.breite - b.width - HINWEIS_RAND))}px`;
      el.style.top = `${Math.max(HINWEIS_RAND, Math.round(bildschirm.hoehe - b.height - HINWEIS_RAND))}px`;
      return;
    }
    const p = platziere(rechteck, { breite: b.width, hoehe: b.height }, bildschirm);
    el.style.left = `${Math.round(p.links)}px`;
    el.style.top = `${Math.round(p.oben)}px`;
  });

  // Fokus auf „Weiter“, solange er nicht bewusst woanders liegt (z. B. im bedienten Ziel).
  const ersterFokus = useRef(true);
  useEffect(() => {
    if (!sichtbar || !weiterKnopf.current) return;
    const aktiv = document.activeElement;
    const verloren = !aktiv || aktiv === document.body || !!karte.current?.contains(aktiv);
    // Beim ersten Zeigen immer (der Fokus liegt noch am Auslöser); danach nur, wenn er nicht im bedienten Ziel steht.
    if (ersterFokus.current || verloren) weiterKnopf.current.focus({ preventScroll: true });
    ersterFokus.current = false;
  }, [sichtbar, index, eingehaengt]);

  // Tastatur: Capture-Phase am Fenster, damit die Tour vor einem offenen Modal reagiert.
  useEffect(() => {
    const beiTaste = (e: KeyboardEvent) => {
      const aktiv = document.activeElement;
      const imPopover = !!karte.current && aktiv instanceof Node && karte.current.contains(aktiv);
      if (e.key === "Escape") {
        if (!imPopover && aktiv instanceof HTMLElement && aktiv.closest(ESC_VORRANG)) return;
        e.preventDefault();
        e.stopPropagation();
        // Esc schließt zuerst das Hornaut-Panel, erst der nächste Esc beendet die Tour.
        if (aktuell.current.hornaut?.panelOffen) aktuell.current.hornaut.beiPanelSchliessen();
        else aktuell.current.beiBeenden();
        return;
      }
      if (e.key === "Tab") {
        if (!imPopover || !karte.current) return;
        const liste = fokussierbareElemente(karte.current);
        if (liste.length === 0) return;
        const erstes = liste[0]!;
        const letztes = liste[liste.length - 1]!;
        if (e.shiftKey && aktiv === erstes) {
          e.preventDefault();
          e.stopPropagation();
          letztes.focus();
        } else if (!e.shiftKey && aktiv === letztes) {
          e.preventDefault();
          e.stopPropagation();
          erstes.focus();
        } else {
          // Tab bleibt im Popover; ein offenes Modal hinter der Tour darf den Fokus nicht an sich ziehen.
          e.stopPropagation();
        }
        return;
      }
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        // In Eingabefeldern (das Ziel bleibt bedienbar) gehören die Pfeile dem Feld.
        if (aktiv instanceof HTMLElement && aktiv.matches(EINGABE)) return;
        // Im Hornaut-Panel und solange es die Karte ersetzt, wechseln die Pfeile den Schritt nicht.
        if (aktiv instanceof HTMLElement && aktiv.closest("[data-hornaut-begleiter]")) return;
        if (aktuell.current.hornaut?.ueberlagert) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.key === "ArrowRight") aktuell.current.beiWeiter();
        else aktuell.current.beiZurueck();
        return;
      }
      if (e.key === "Enter" && aktiv === karte.current) {
        e.preventDefault();
        e.stopPropagation();
        aktuell.current.beiWeiter();
      }
    };
    window.addEventListener("keydown", beiTaste, true);
    return () => window.removeEventListener("keydown", beiTaste, true);
  }, []);

  if (!eingehaengt || !schritt) return null;

  const letzter = index >= schritte.length - 1;
  const loch = mitZiel && rechteck ? lochUmZiel(rechteck, bildschirm) : null;
  // Ziel nicht gefunden: nicht-modaler Hinweis ohne Dimmer; die Seite bleibt bedienbar (die Handlung des Schritts ist ihr Weg zum Ziel).
  // Auch wenn das Ziel nach dem Finden verschwindet (Status bleibt "da", das Rechteck wird null): derselbe nicht-modale Hinweis,
  // nie die modale Zentral-Karte mit Voll-Dimmer (die gehört nur den Schritten ohne Ziel).
  const hinweis = (zielStatus === "fehlt" || (mitZiel && !rechteck)) && !loch;
  const zentral = !loch && !hinweis;
  const ansage = `Schritt ${index + 1} von ${schritte.length}`;
  // Figur nur in der Zentral-Karte (Einleitung, Abschluss) und nur mit Hornaut (KI an).
  const mitFigur = zentral && !!hornaut?.figur;

  const textBereich = (
    <>
      <div className="px-4 pt-3 flex items-center justify-between gap-x-2">
        <p className="min-w-0 truncate text-xs text-muted-foreground-1" title={`Tour · ${titel}`}>
          Tour · {kopfzeile ?? titel}
        </p>
        <Knopf variante="ghost" nurSymbol groesse="sm" starr label="Tour beenden" symbol={<SchliessenSymbol />} onClick={beiBeenden} />
      </div>
      <div className="px-4 pb-4 max-h-80 overflow-y-auto">
        <div className="flex items-start gap-x-3">
          {mitFigur && (
            <span className="ai-gated shrink-0 md:hidden" data-ki="gated" data-ki-aktiv="an">
              <HornautFigurBild hoehe={104} />
            </span>
          )}
          <div className="min-w-0">
            <h2 id={`${id}-titel`} className="font-semibold text-foreground">
              {schritt.titel}
            </h2>
            <p id={`${id}-text`} className="mt-1 text-sm text-foreground">
              {schritt.text}
            </p>
          </div>
        </div>
        {hinweis && (
          <p className="mt-2 text-sm text-muted-foreground-1" data-tour-hinweis="ziel-fehlt">
            Diese Stelle ist gerade nicht sichtbar. Du kannst trotzdem weitergehen.
          </p>
        )}
        {hornaut?.zeile && (
          <p className="ai-gated mt-3 flex items-start gap-x-2 rounded-lg bg-primary-100 py-2 px-3 text-sm text-primary-800" data-ki="gated" data-ki-aktiv="an" data-hornaut-zeile>
            <KiSymbol className="mt-0.5 shrink-0 size-4 text-ki" />
            <span>{hornaut.zeile}</span>
          </p>
        )}
      </div>
      {hornaut?.knopf && (
        <div className="px-4 pb-3">
          {/* KI-Auslöser (R-D): `.ai-gated` am Element, `.ai-mark` als Auslöser; Kopf-Avatar und Lucide Sparkles */}
          <Knopf
            ref={hornautKnopf}
            ki
            groesse="sm"
            daten={{ "data-ki": "ausloeser", "data-hornaut-knopf": true }}
            ariaHaspopup="dialog"
            ariaExpanded={hornaut.panelOffen}
            onClick={hornaut.beiFragen}
            symbol={
              // eslint-disable-next-line @next/next/no-img-element -- statisches Kopf-Bild der Marke
              <img src={HORNAUT_KOPF_AVATAR} alt="" aria-hidden="true" width={22} height={22} className="shrink-0 size-5.5 rounded-full object-cover ring-1 ring-ki-line bg-ki-soft" />
            }
          >
            Hornaut fragen
            <KiSymbol className="shrink-0 size-4 text-ki" />
          </Knopf>
        </div>
      )}
      <div className="px-4 pb-3 flex items-center gap-x-3">
        <p className="text-xs text-muted-foreground-1" role="status" aria-live="polite">
          {ansage}
        </p>
        <div className="flex flex-wrap items-center gap-1" aria-hidden="true">
          {schritte.map((_, i) => (
            <span key={i} className={i === index ? "h-1.5 w-4 rounded-full bg-primary" : "size-1.5 rounded-full bg-muted-focus"} />
          ))}
        </div>
      </div>
      <div className="py-3 px-4 border-t border-popover-line flex items-center justify-between gap-x-2">
        {letzter ? (
          <span />
        ) : (
          <Knopf variante="ghost" onClick={beiBeenden}>
            Beenden
          </Knopf>
        )}
        <div className="flex items-center gap-x-2">
          <Knopf onClick={beiZurueck} disabled={index === 0} symbol={<ZurueckSymbol />}>
            Zurück
          </Knopf>
          <Knopf ref={weiterKnopf} variante="primaer" onClick={beiWeiter}>
            {letzter ? "Fertig" : "Weiter"}
            <WeiterSymbol />
          </Knopf>
        </div>
      </div>
    </>
  );

  // Hornaut-Panel überlagert die Fläche (Sheet mobil, 768 bis 1279 px): von der Tour bleibt nur die stehende Leiste.
  if (ueberlagert && hornaut) {
    return createPortal(
      <div className={LEISTE} role="region" aria-label="Tour" data-tour-overlay="leiste">
        <p className="min-w-0 truncate text-sm text-foreground" role="status" aria-live="polite">
          Tour · {ansage}
        </p>
        <Knopf onClick={hornaut.beiZurTour} daten={{ "data-tour-zur-tour": true }}>
          Zur Tour
        </Knopf>
      </div>,
      document.body,
    );
  }

  return createPortal(
    zielStatus === "suche" ? (
      <p className="sr-only" role="status" aria-live="polite">
        Die Stelle für diesen Schritt wird gesucht.
      </p>
    ) : (
      <>
        {!hinweis && <TourDimmer loch={loch} bildschirm={bildschirm} />}
        {/* Preline: component/popover/popover-with-content (Optik des Popovers; Position setzt der Baustein, Inhalt und Texte eigen) */}
        <div
          ref={karte}
          className={zentral ? (mitFigur ? KARTE_ZENTRAL_FIGUR : KARTE_ZENTRAL) : KARTE_ZIEL}
          role="dialog"
          aria-modal={zentral ? "true" : undefined}
          aria-labelledby={`${id}-titel`}
          aria-describedby={`${id}-text`}
          tabIndex={-1}
          data-tour-overlay={hinweis ? "hinweis" : zentral ? "zentral" : "ziel"}
        >
          {mitFigur ? (
            <div className="flex min-h-0 grow">
              {/* Figur (Marke, `.ai-gated`): links, ab 768 px; mobil klein neben dem Titel. Abweichung AW-015c: Figur-Datei ohne Claude-Design-Freigabe */}
              <div className="ai-gated max-md:hidden w-52 shrink-0 flex items-center justify-center bg-primary-50 rounded-s-xl" data-ki="gated" data-ki-aktiv="an" data-hornaut-figur>
                <HornautFigurBild hoehe={300} />
              </div>
              <div className="min-w-0 grow flex flex-col">{textBereich}</div>
            </div>
          ) : (
            textBereich
          )}
        </div>
      </>
    ),
    document.body,
  );
}
